/**
 * Plan Tier Definitions
 *
 * Defines the plan tiers and generates usage buckets for bucket-based pricing.
 *
 * @module @hummingbird/pricing/plans
 */

import type { PlanId, PlanTier, UsageBucket, BillingInterval } from './types.js';

// =============================================================================
// Plan Tier Definitions
// =============================================================================

export const PLAN_TIERS: Record<PlanId, PlanTier> = {
  free: {
    id: 'free',
    name: 'Free',
    description: 'Validate your integration before you commit',
    minRequests: 0,
    maxRequests: 2000, // 1K base + 1K bonus
    bucketIncrement: 0,
    basePriceCents: 0,
    priceIncrementCents: 0,
    order: 0,
    supportsTeamMembers: false,
    features: {
      maxApiKeys: 1,
      rateLimit: 5, // 50 req/10s burst limit
      sla: null,
      premiumApis: false,
      bulkEndpoints: false,
      sso: false,
      defaultSupportLevel: 'community',
    },
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    description: 'Ship validation to production',
    minRequests: 10001,
    maxRequests: 50000,
    bucketIncrement: 5000,
    basePriceCents: 599, // $5.99
    priceIncrementCents: 100, // $1.00 per 5K
    order: 1,
    supportsTeamMembers: false,
    features: {
      maxApiKeys: 5,
      rateLimit: 25, // 250 req/10s burst limit
      sla: null,
      premiumApis: false,
      bulkEndpoints: false,
      sso: false,
      defaultSupportLevel: 'email_48h',
    },
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'Consolidate your vendor stack',
    tagline: 'Most Popular',
    minRequests: 50001,
    maxRequests: 250000,
    bucketIncrement: 10000,
    basePriceCents: 1299, // $12.99
    priceIncrementCents: 100, // $1.00 per 10K
    order: 2,
    supportsTeamMembers: true,
    features: {
      maxApiKeys: 20,
      rateLimit: 50, // 500 req/10s burst limit
      sla: null,
      premiumApis: true,
      bulkEndpoints: true,
      sso: false,
      defaultSupportLevel: 'email_24h',
    },
  },
  elite: {
    id: 'elite',
    name: 'Elite',
    description: 'Production-grade, every endpoint, one invoice',
    minRequests: 250001,
    maxRequests: 2000000,
    bucketIncrement: 25000,
    basePriceCents: 3199, // $31.99
    priceIncrementCents: 100, // $1.00 per 25K
    order: 3,
    supportsTeamMembers: true,
    features: {
      maxApiKeys: 50,
      rateLimit: 250, // 2500 req/10s burst limit
      sla: 99.9,
      premiumApis: true,
      bulkEndpoints: true,
      sso: false,
      defaultSupportLevel: 'priority_4h',
    },
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Custom scale, dedicated support, your terms',
    minRequests: 2000001,
    maxRequests: Infinity,
    bucketIncrement: 100000,
    basePriceCents: 9999, // $99.99
    priceIncrementCents: 400, // $4.00 per 100K
    order: 4,
    supportsTeamMembers: true,
    features: {
      maxApiKeys: -1, // Unlimited
      rateLimit: -1, // Custom
      sla: 99.99,
      premiumApis: true,
      bulkEndpoints: true,
      sso: true,
      defaultSupportLevel: 'dedicated',
    },
  },
};

// =============================================================================
// Plan Tier Helpers
// =============================================================================

/**
 * Get ordered list of plan tiers for display
 */
export function getOrderedPlanTiers(): PlanTier[] {
  return Object.values(PLAN_TIERS).sort((a, b) => a.order - b.order);
}

/**
 * Get the plan order for comparison
 */
export function getPlanOrder(planId: PlanId): number {
  return PLAN_TIERS[planId]?.order ?? 0;
}

/**
 * Check if one plan is higher tier than another
 */
export function isHigherTier(planA: PlanId, planB: PlanId): boolean {
  return getPlanOrder(planA) > getPlanOrder(planB);
}

/**
 * Get the next tier up from current plan
 */
export function getNextTier(currentPlan: PlanId): PlanId | null {
  const order = getPlanOrder(currentPlan);
  const nextTier = Object.values(PLAN_TIERS).find((t) => t.order === order + 1);
  return nextTier?.id || null;
}

/**
 * Check if a plan supports team members
 */
export function planSupportsTeamMembers(planId: PlanId): boolean {
  return PLAN_TIERS[planId]?.supportsTeamMembers ?? false;
}

// =============================================================================
// Usage Bucket Generation
// =============================================================================

/**
 * Format number with K/M suffix
 */
export function formatRequestCount(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`.replace('.0M', 'M');
  }
  if (num >= 1000) {
    return `${Math.round(num / 1000)}K`;
  }
  return num.toString();
}

/**
 * Generate all usage buckets for a plan tier
 */
function generateBucketsForPlan(planId: PlanId): UsageBucket[] {
  const tier = PLAN_TIERS[planId];
  if (planId === 'free' || tier.bucketIncrement === 0) return [];

  const buckets: UsageBucket[] = [];

  let currentMax = tier.minRequests + tier.bucketIncrement - 1;
  let bucketIndex = 0;

  while (currentMax <= tier.maxRequests) {
    const monthlyPrice = tier.basePriceCents + bucketIndex * tier.priceIncrementCents;
    const introPrice = Math.round(monthlyPrice * 0.8); // 20% off
    const annualPrice = Math.round(monthlyPrice * 10); // 17% off (10 months)

    const maxK = Math.round(currentMax / 1000);
    const lookupKey = `${planId}_${maxK}k_monthly`;

    buckets.push({
      plan: planId,
      maxRequests: currentMax,
      monthlyPriceCents: monthlyPrice,
      introPriceCents: introPrice,
      annualPriceCents: annualPrice,
      stripeLookupKey: lookupKey,
      label: `${formatRequestCount(currentMax)} requests`,
    });

    currentMax += tier.bucketIncrement;
    bucketIndex++;

    // Safety limit for Enterprise (cap at 30 buckets)
    if (planId === 'enterprise' && bucketIndex >= 30) break;
  }

  return buckets;
}

// =============================================================================
// All Usage Buckets (Generated)
// =============================================================================

/**
 * Free tier bucket for slider display
 * This is a synthetic bucket for the slider UI - free tier doesn't have Stripe pricing
 */
const FREE_TIER_BUCKET: UsageBucket = {
  plan: 'free',
  maxRequests: 2000,
  monthlyPriceCents: 0,
  introPriceCents: 0,
  annualPriceCents: 0,
  stripeLookupKey: 'free',
  label: '2K requests',
};

export const USAGE_BUCKETS: UsageBucket[] = [
  FREE_TIER_BUCKET,
  ...generateBucketsForPlan('starter'),
  ...generateBucketsForPlan('pro'),
  ...generateBucketsForPlan('elite'),
  ...generateBucketsForPlan('enterprise'),
];

// =============================================================================
// Bucket Helpers
// =============================================================================

/**
 * Get buckets for a specific plan
 */
export function getBucketsForPlan(planId: PlanId): UsageBucket[] {
  return USAGE_BUCKETS.filter((b) => b.plan === planId);
}

/**
 * Get the first (base) bucket for a plan
 */
export function getBaseBucketForPlan(planId: PlanId): UsageBucket | null {
  const buckets = getBucketsForPlan(planId);
  return buckets[0] || null;
}

/**
 * Find the bucket that matches a request count
 * Returns the smallest bucket that can accommodate the usage
 */
export function getBucketForUsage(requests: number): UsageBucket | null {
  return USAGE_BUCKETS.find((b) => requests <= b.maxRequests) || null;
}

/**
 * Find the appropriate bucket considering current plan and bucket (sticky)
 *
 * Rules:
 * 1. Cannot downgrade to lower tier (plan order)
 * 2. Cannot downgrade to smaller bucket (sticky buckets)
 * 3. Must accommodate current usage
 */
export function getTargetBucket(
  currentUsage: number,
  currentPlan: PlanId,
  currentBucketMax: number
): UsageBucket | null {
  const currentPlanOrder = getPlanOrder(currentPlan);

  const eligibleBuckets = USAGE_BUCKETS.filter(
    (b) =>
      currentUsage <= b.maxRequests &&
      getPlanOrder(b.plan) >= currentPlanOrder &&
      b.maxRequests >= currentBucketMax
  );

  return eligibleBuckets[0] || null;
}

/**
 * Check if usage exceeds current bucket, requiring an upgrade
 */
export function needsBucketUpgrade(currentUsage: number, currentBucketMax: number): boolean {
  return currentUsage > currentBucketMax;
}

/**
 * Check if usage exceeds tier ceiling, requiring tier upgrade
 */
export function needsTierUpgrade(currentUsage: number, currentPlan: PlanId): boolean {
  const tier = PLAN_TIERS[currentPlan];
  return currentUsage > tier.maxRequests;
}

// =============================================================================
// Pricing Display Helpers
// =============================================================================

/**
 * Format price from cents to display string
 */
export function formatPrice(cents: number): string {
  const dollars = cents / 100;
  // Remove trailing .00 for whole dollar amounts
  if (dollars === Math.floor(dollars)) {
    return `$${dollars}`;
  }
  return `$${dollars.toFixed(2)}`;
}

/**
 * Get display price for a bucket
 */
export function getBucketDisplayPrice(
  bucket: UsageBucket,
  interval: BillingInterval,
  isIntro: boolean = false
): { price: string; originalPrice?: string; perMonth?: string } {
  if (interval === 'annual') {
    const annualPrice = formatPrice(bucket.annualPriceCents);
    const perMonth = formatPrice(Math.round(bucket.annualPriceCents / 12));
    return {
      price: annualPrice,
      perMonth: `${perMonth}/mo`,
    };
  }

  const monthlyPrice = formatPrice(bucket.monthlyPriceCents);

  if (isIntro) {
    const introPrice = formatPrice(bucket.introPriceCents);
    return {
      price: introPrice,
      originalPrice: monthlyPrice,
    };
  }

  return { price: monthlyPrice };
}

/**
 * Get the starting price for a plan tier
 */
export function getPlanStartingPrice(planId: PlanId, interval: BillingInterval = 'monthly'): string {
  if (planId === 'free') return 'Free';
  if (planId === 'enterprise') return 'Custom';

  const baseBucket = getBaseBucketForPlan(planId);
  if (!baseBucket) return 'N/A';

  const price =
    interval === 'annual' ? baseBucket.annualPriceCents : baseBucket.monthlyPriceCents;

  return formatPrice(price);
}

// =============================================================================
// Stripe Helpers
// =============================================================================

/**
 * Get Stripe lookup key for a bucket and interval
 */
export function getStripeLookupKey(bucket: UsageBucket, interval: BillingInterval): string {
  if (interval === 'annual') {
    return bucket.stripeLookupKey.replace('_monthly', '_annual');
  }
  return bucket.stripeLookupKey;
}

/**
 * Get all Stripe price lookup keys for creating prices
 */
export function getAllStripeLookupKeys(): string[] {
  const keys: string[] = [];

  for (const bucket of USAGE_BUCKETS) {
    keys.push(bucket.stripeLookupKey);
    keys.push(bucket.stripeLookupKey.replace('_monthly', '_annual'));
  }

  return keys;
}

// =============================================================================
// Slider Stops (UI Only - finer granularity for UX)
// =============================================================================

/**
 * Slider stop for the pricing UI
 * These are separate from billing buckets - purely for visual/UX purposes
 */
export interface SliderStop {
  /** Request count at this stop */
  requests: number;
  /** Which plan this falls into */
  plan: PlanId;
  /** Display label */
  label: string;
}

/**
 * Generate slider stops with finer granularity at the low end
 * 1K increments from 1K-10K, then coarser increments for higher tiers
 */
export function generateSliderStops(): SliderStop[] {
  const stops: SliderStop[] = [];

  // 1K to 10K in 1K increments (all map to free or starter)
  for (let i = 1; i <= 10; i++) {
    const requests = i * 1000;
    stops.push({
      requests,
      plan: requests <= 2000 ? 'free' : 'starter',
      label: formatRequestCount(requests),
    });
  }

  // 15K to 50K in 5K increments (starter tier)
  for (let i = 15000; i <= 50000; i += 5000) {
    stops.push({
      requests: i,
      plan: 'starter',
      label: formatRequestCount(i),
    });
  }

  // 60K to 250K in 10K increments (pro tier) - sample key points
  for (const i of [60000, 80000, 100000, 150000, 200000, 250000]) {
    stops.push({
      requests: i,
      plan: 'pro',
      label: formatRequestCount(i),
    });
  }

  // 300K to 2M in larger increments (elite tier) - sample key points
  for (const i of [300000, 500000, 750000, 1000000, 1500000, 2000000]) {
    stops.push({
      requests: i,
      plan: 'elite',
      label: formatRequestCount(i),
    });
  }

  // 2.5M to 5M+ (enterprise tier) - sample key points
  for (const i of [2500000, 3000000, 4000000, 5000000]) {
    stops.push({
      requests: i,
      plan: 'enterprise',
      label: formatRequestCount(i),
    });
  }

  return stops;
}

/** Pre-generated slider stops */
export const SLIDER_STOPS = generateSliderStops();

/**
 * Get the plan that should be active for a given request count
 */
export function getPlanForRequestCount(requests: number): PlanId {
  if (requests <= 2000) return 'free';
  if (requests <= 50000) return 'starter';
  if (requests <= 250000) return 'pro';
  if (requests <= 2000000) return 'elite';
  return 'enterprise';
}

/**
 * Check if a plan should be disabled based on slider position
 * A plan is disabled when the slider exceeds its max requests
 */
export function isPlanDisabledAtRequestCount(planId: PlanId, requests: number): boolean {
  const tier = PLAN_TIERS[planId];
  return requests > tier.maxRequests;
}

/**
 * Get the bucket for a given request count and plan
 * Returns the appropriate bucket or null if the plan doesn't support that count
 */
export function getBucketForRequestCount(
  requests: number,
  planId: PlanId
): UsageBucket | null {
  if (planId === 'free') {
    return USAGE_BUCKETS.find((b) => b.plan === 'free') || null;
  }

  // Find the smallest bucket in this plan that can accommodate the requests
  const planBuckets = USAGE_BUCKETS.filter((b) => b.plan === planId);
  return planBuckets.find((b) => requests <= b.maxRequests) || planBuckets[planBuckets.length - 1] || null;
}

/**
 * Calculate intro price (20% off)
 */
export function calculateIntroPrice(priceCents: number): number {
  return Math.round(priceCents * 0.8);
}

/**
 * Calculate savings amount
 */
export function calculateSavings(originalCents: number, discountedCents: number): number {
  return originalCents - discountedCents;
}
